/**
 * RouteCorridorList - Responsive list for route corridors
 * Mobile/Tablet: cards · Desktop: table
 */

import React from "react";
import { Table, Tag, Button, Space, Popconfirm, Tooltip, Empty } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { Typography } from "antd";
import { Box, Grid, CircularProgress } from "@mui/material";
import { format, formatDistanceToNow } from "date-fns";
import { useDeviceType } from "../../leads/hooks/useDeviceType";
import RouteCorridorCard from "./RouteCorridorCard";

const { Text } = Typography;

const RouteCorridorList = ({
  routeCorridors,
  loading,
  onEdit,
  onDelete,
}) => {
  const { isMobile, isTablet } = useDeviceType();

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: {
        showTitle: false,
      },
      render: (text) => (
        <Tooltip placement="topLeft" title={text}>
          {text || "-"}
        </Tooltip>
      ),
    },
    {
      title: "Provinces",
      dataIndex: "provinces",
      key: "provinces",
      render: (provinces) => (
        <Space wrap>
          {provinces && provinces.length > 0 ? (
            provinces.map((province) => (
              <Tag key={province} color="blue">
                {province}
              </Tag>
            ))
          ) : (
            <Text type="secondary">-</Text>
          )}
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "active",
      key: "active",
      filters: [
        { text: "Active", value: true },
        { text: "Inactive", value: false },
      ],
      onFilter: (value, record) => record.active === value,
      render: (active) => (
        <Tag
          color={active ? "green" : "red"}
          icon={active ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
        >
          {active ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      render: (date) => {
        if (!date) return <Text type="secondary">-</Text>;
        const dateObj = new Date(date);
        return (
          <Tooltip title={format(dateObj, "PPpp")}>
            <Text type="secondary">
              {formatDistanceToNow(dateObj, { addSuffix: true })}
            </Text>
          </Tooltip>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 160,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => onEdit(record)}
            size="small"
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete Route Corridor"
            description={`Are you sure you want to delete "${record.name}"? This action cannot be undone.`}
            onConfirm={() => onDelete(record)}
            okText="Yes"
            cancelText="No"
            okType="danger"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (isMobile || isTablet) {
    if (loading && (!routeCorridors || routeCorridors.length === 0)) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!loading && (!routeCorridors || routeCorridors.length === 0)) {
      return (
        <Empty
          description="No route corridors found"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      );
    }

    return (
      <Grid container spacing={1.5}>
        {routeCorridors.map((routeCorridor) => (
          <Grid
            item
            xs={12}
            sm={isTablet ? 6 : 12}
            key={routeCorridor.id}
          >
            <RouteCorridorCard
              routeCorridor={routeCorridor}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (!loading && routeCorridors.length === 0) {
    return (
      <Empty
        description="No route corridors found"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  return (
    <Table
      columns={columns}
      dataSource={routeCorridors}
      loading={loading}
      rowKey="id"
      pagination={{
        pageSize: 20,
        showSizeChanger: true,
        showTotal: (total) =>
          `Total ${total} route corridor${total !== 1 ? "s" : ""}`,
        pageSizeOptions: ["10", "20", "50", "100"],
      }}
      scroll={{ x: true }}
      size="middle"
      bordered={false}
    />
  );
};

export default RouteCorridorList;
